import {useEffect, useState, type FC} from 'react'
import {Form, Modal, Nav, Spinner} from 'react-bootstrap'
import {useCreateBrandVoiceMutation} from '../../../../services/brandVoiceServiceApi'
import classes from '../MembersArea.module.scss'
import * as Yup from 'yup'
import {toast} from 'react-toastify'
import {useAuth} from '../../../../app/modules/auth/core/Auth'
import {GlobalErrorMessagesEnum} from '../../../../types/types'
import {CustomTooltip} from '../../universal-components/tooltip/Tooltip'

// Define Yup validation schema
const brandVoiceSchema = Yup.object().shape({
  name: Yup.string()
    .required('Brand voice name is required')
    .min(3, 'Name must be at least 3 characters')
    .max(100, 'Name cannot exceed 100 characters'),
  content: Yup.string()
    .required('Content is required')
    .test('content-length', function (value) {
      const {type} = this.parent
      const [min, max] = type === 'content' ? [500, 2000] : [50, 500]
      if (!value || value.length < min || value.length > max) {
        return this.createError({message: `Content must be ${min}-${max} characters`})
      }
      return true
    }),
})

const BrandVoiceTrainingModal = ({
  setShowTrainingModal,
  showTrainingModal,
  refetch,
  data,
}: {
  showTrainingModal: any
  setShowTrainingModal: any
  refetch: any
  data: any
}) => {
  const [submitBrandVoice, {isLoading}] = useCreateBrandVoiceMutation()
  const [type, setType] = useState('content')
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({})
  const [serverErrors, setServerErrors] = useState<string[]>([])
  const [content, setContent] = useState('')
  const [name, setName] = useState('')
  const [handlePostType, setHandlePostType] = useState('NEW_POST')
  const [brandVoiceId, setBrandVoiceId] = useState(null)

  useEffect(() => {
    if (data) {
      setName(data.name)
      setContent(data.personality_traits)
      setBrandVoiceId(data.id)
      setHandlePostType('OLD_POST')
    }
  }, [data])

  const validateField = async (field: any, value: any) => {
    try {
      type FieldKey = keyof typeof brandVoiceSchema.fields

      const fieldSchema = Yup.object().shape({
        [field as FieldKey]: brandVoiceSchema.fields[field as FieldKey],
      })

      await fieldSchema.validate({[field]: value, type}, {abortEarly: false})
      setValidationErrors((prev) => ({...prev, [field]: ''}))

      return true
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const errorMessage = error.inner[0]?.message || ''
        setValidationErrors((prev) => ({...prev, [field]: errorMessage}))
      }
      return false
    }
  }

  const handleInputChange = async (field: string, value: string) => {
    if (field === 'name') {
      setName(value)
    } else if (field === 'content') {
      setContent(value)
    } else if (field === 'type') {
      setType(value)
    }

    // Validate the field after it changes
    await validateField(field, value)
  }

  const handleSubmit = async (e: any) => {
    e.preventDefault()

    // Prepare form data
    const formData = {
      id: brandVoiceId,
      name,
      content,
      type,
    }

    try {
      // Validate all fields before submission
      await brandVoiceSchema.validate(formData, {abortEarly: false})

      // If validation passes, submit the form
      submitBrandVoice(formData)
        .unwrap()
        .then((data) => {
          refetch()
          toast.success(
            `Brand voice "${data?.name && data.name.slice(0, 10)}" created successfully`
          )
          handleClose()
        })
        .catch((error) => {
          console.log(error)
          if (error.status === 400) {
            setServerErrors(error.data.message)
            toast.error(error.data.message[0])
            return
          }
          toast.error(GlobalErrorMessagesEnum.SERVER_ERROR)
        })
    } catch (validationError) {
      if (validationError instanceof Yup.ValidationError) {
        // Transform validation errors into a more usable format
        const errors: {[key: string]: string} = {}
        validationError.inner.forEach((err) => {
          if (err.path) {
            errors[err.path] = err.message
          }
        })

        setValidationErrors(errors)
      }
    }
  }

  const handleClose = () => {
    setName('')
    setContent('')
    setValidationErrors({})
    setServerErrors([])
    setShowTrainingModal(false)
    setBrandVoiceId(null)
  }

  return (
    <>
      <Modal show={showTrainingModal} onHide={handleClose} centered backdrop='static'>
        <Modal.Header
          style={{
            border: 'none',
          }}
          closeButton
        >
          <Modal.Title>{!data ? 'Add Brand Voice' : 'Edit Brand Voice'} </Modal.Title>
        </Modal.Header>

        <form onSubmit={handleSubmit}>
          <Modal.Body className='px-4 py-3'>
            {/* Brand Voice Name Field */}
            <div className='mb-4'>
              <div className='d-flex align-items-center mb-2'>
                <label htmlFor='brandVoiceName' className='form-label mb-0 fw-medium'>
                  Brand Voice Name <span className='text-danger'>*</span>
                </label>
                <CustomTooltip
                  tooltipText='Enter a name to identify this brand voice profile'
                  tooltipAnchor='formName'
                />
              </div>
              <input
                id='brandVoiceName'
                name='name'
                type='text'
                className={`form-control p-3 ${validationErrors.name ? 'is-invalid' : ''}`}
                placeholder='E.g., Professional, Casual, Friendly'
                style={{
                  borderRadius: '8px',
                  border: '1px solid #ced4da',
                  outline: 'none',
                }}
                value={name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                onBlur={() => validateField('name', name)}
              />
              {validationErrors.name && (
                <div className='invalid-feedback'>{validationErrors.name}</div>
              )}
            </div>

            {!data && (
              <div className='mb-4'>
                <div className='d-flex align-items-center mb-2'>
                  <label className='form-label mb-0 fw-medium'>
                    Type <span className='text-danger'>*</span>
                  </label>
                  <CustomTooltip
                    tooltipText='Choose what type of content you want to provide'
                    tooltipAnchor='contentTypeToggle'
                  />
                </div>
                <Nav
                  variant='tabs'
                  className='mb-3'
                  activeKey={type}
                  onSelect={(selectedKey) => handleInputChange('type', selectedKey as string)}
                >
                  <Nav.Item>
                    <Nav.Link eventKey='content'>Content</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey='brand_voice'>Brand Voice</Nav.Link>
                  </Nav.Item>
                </Nav>
                {validationErrors.type && (
                  <div className='text-danger small mt-1'>{validationErrors.type}</div>
                )}
              </div>
            )}

            {/* Content Field */}
            <div className='mb-3'>
              <div className='d-flex align-items-center mb-2'>
                <label htmlFor='brandContent' className='form-label mb-0 fw-medium'>
                  {type === 'content' ? 'Content for Analysis' : 'Brand Voice Content'}{' '}
                  <span className='text-danger'>*</span>
                </label>
                <CustomTooltip
                  tooltipText={
                    type === 'content'
                      ? 'Enter the content you want to work with'
                      : "Provide content that represents your brand's tone and style"
                  }
                  tooltipAnchor='formContent'
                />
              </div>
              <Form.Control
                id='brandContent'
                name='content'
                as='textarea'
                className={`p-3 ${validationErrors.content ? 'is-invalid' : ''}`}
                placeholder={
                  type === 'content'
                    ? 'Enter your content here. For best results, include 500-2000 characters.'
                    : 'Enter text that represents your brand voice. No analysis will be performed.'
                }
                style={{
                  height: '150px',
                  borderRadius: '8px',
                  resize: 'vertical',
                }}
                value={content}
                onChange={(e) => handleInputChange('content', e.target.value)}
                onBlur={() => validateField('content', content)}
              />
              {validationErrors.content && (
                <div className='invalid-feedback'>{validationErrors.content}</div>
              )}
              <div className='text-muted mt-2 small d-flex justify-content-between'>
                <span>Min: {type === 'content' ? 500 : 50} characters</span>
                <span>
                  {content.length}/{type === 'content' ? 2000 : 500} characters
                </span>
              </div>
            </div>
          </Modal.Body>

          <Modal.Footer
            style={{
              padding: '1rem',
              borderTop: 'none',
              gap: '16px',
            }}
          >
            <div className={classes.maping__modalSubmitOuter}>
              <button
                type='button'
                className={classes.maping__modalCloseButton}
                onClick={handleClose}
              >
                Close
              </button>
            </div>
            <div className={classes.maping__modalSubmitOuter}>
              <button
                type='submit'
                className='btn btn-primary px-4 h-100 py-2'
                disabled={isLoading}
              >
                {isLoading ? (
                  <Spinner animation='border' role='status' size='sm'>
                    <span className='visually-hidden'>Loading...</span>
                  </Spinner>
                ) : (
                  'Submit'
                )}
              </button>
            </div>
          </Modal.Footer>
        </form>
      </Modal>
    </>
  )
}

export default BrandVoiceTrainingModal
